import React from 'react';

import Autocomplete, { autocompleteClasses } from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import CircularProgress from '@mui/material/CircularProgress';
import ListSubheader from '@mui/material/ListSubheader';
import Popper from '@mui/material/Popper';
import { styled, useTheme } from '@mui/material/styles';
import TextField from '@mui/material/TextField';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { VariableSizeList } from 'react-window';

import { sharedClasses } from './sharedClasses';

import type { AutocompleteInputChangeReason, AutocompleteProps } from '@mui/material/Autocomplete';
import type { SxProps } from '@mui/material/styles';
import type { AutocompleteValue } from '@mui/material/useAutocomplete';
import type { ListChildComponentProps } from 'react-window';

interface VirtualizeOptionProps<T, FreeSolo extends boolean> {
  options: T[];
  value: AutocompleteValue<T, false, false, FreeSolo>;
  onChange: (value: AutocompleteValue<T, false, false, FreeSolo>) => void | Promise<void>;
  getOptionLabel: NonNullable<AutocompleteProps<T, false, false, FreeSolo>['getOptionLabel']>;
  isOptionEqualToValue?: AutocompleteProps<T, false, false, FreeSolo>['isOptionEqualToValue'];
  groupBy?: (option: T) => string;
  label?: string;
  error?: boolean | string;
  errorStyles?: SxProps;
  loading?: boolean;
  loadingText?: string;
  freeSolo?: FreeSolo;
  dataTestId?: string;
  styles?: SxProps;
  required?: boolean;
  placeholder?: string;
  innerRef?: React.RefObject<HTMLDivElement | null>;
  disablePortal?: boolean;
  inputAutoComplete?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  disableClientFilter?: boolean;
  noOptionsText?: string;
  onInputChange?: (value: string, reason: AutocompleteInputChangeReason) => void;
  onListboxScroll?: React.UIEventHandler<HTMLElement>;
}

export type VirtualizeGenericProps<T, FreeSolo extends boolean = false> = VirtualizeOptionProps<
  T,
  FreeSolo
> &
  (boolean extends FreeSolo
    ? { freeSolo: FreeSolo }
    : FreeSolo extends false
      ? { freeSolo?: false }
      : { freeSolo: FreeSolo });

export interface VirtualizeLegacyProps {
  passOptions: string[];
  value?: string | null;
  label?: string;
  error?: boolean | string;
  loading?: boolean;
  hasIdAndRef?: boolean;
  loadingText?: string;
  setValue: ((value: string) => void) | ((event: string, value: string) => void);
  passedStyles?: SxProps;
  errorStyles?: SxProps;
  freeSolo?: boolean;
  dataTestId?: string;
}

export type VirtualizeProps<T, FreeSolo extends boolean = false> =
  | VirtualizeGenericProps<T, FreeSolo>
  | VirtualizeLegacyProps;

interface NormalizedVirtualizeProps<T, FreeSolo extends boolean> {
  dataTestId: string;
  disableClientFilter: boolean;
  disablePortal: boolean;
  error?: boolean | string;
  errorStyles?: SxProps;
  forcePopupIcon?: boolean;
  freeSolo?: FreeSolo;
  getOptionLabel: NonNullable<AutocompleteProps<T, false, false, FreeSolo>['getOptionLabel']>;
  groupBy?: (option: T) => string;
  innerRef?: React.RefObject<HTMLDivElement | null>;
  inputAutoComplete?: string;
  inputRef?: React.RefObject<HTMLInputElement>;
  isOptionEqualToValue?: AutocompleteProps<T, false, false, FreeSolo>['isOptionEqualToValue'];
  label?: string;
  loading?: boolean;
  loadingText?: string;
  noOptionsText?: string;
  onChange: NonNullable<AutocompleteProps<T, false, false, FreeSolo>['onChange']>;
  onInputChange?: (
    event: React.SyntheticEvent,
    value: string,
    reason: AutocompleteInputChangeReason
  ) => void;
  onListboxScroll?: React.UIEventHandler<HTMLElement>;
  options: T[];
  placeholder?: string;
  required: boolean;
  styles?: SxProps;
  value: AutocompleteValue<T, false, false, FreeSolo>;
}

const isLegacyProps = <T, FreeSolo extends boolean>(
  props: VirtualizeProps<T, FreeSolo>
): props is VirtualizeLegacyProps => 'passOptions' in props;

const RenderRow = (props: ListChildComponentProps) => {
  const [displayTooltip, setDisplayTooltip] = React.useState(false);
  const { data, index, style } = props;
  const dataSet = data[index];
  const inlineStyle = {
    ...style,
    top: (style.top as number) + 8
  };

  function refCallback(ref: HTMLElement) {
    if (!ref) return;
    if (ref.parentElement && ref.clientWidth > ref.parentElement.clientWidth) {
      setDisplayTooltip(true);
    }
  }

  if (Object.hasOwn(dataSet, 'group')) {
    return (
      <ListSubheader key={dataSet.key} component="div" style={inlineStyle}>
        <Tooltip
          enterDelay={600}
          placement="bottom-start"
          title={displayTooltip ? dataSet.group : ''}
        >
          <div ref={refCallback}>{dataSet.group}</div>
        </Tooltip>
      </ListSubheader>
    );
  }

  return (
    <Typography
      component="li"
      {...dataSet[0]}
      noWrap
      data-testid={`option-${dataSet[1]}`}
      style={inlineStyle}
    >
      <Tooltip enterDelay={600} placement="bottom-start" title={displayTooltip ? dataSet[1] : ''}>
        <div ref={refCallback}>{dataSet[1]}</div>
      </Tooltip>
    </Typography>
  );
};

const OuterElementContext = React.createContext<React.HTMLAttributes<HTMLElement>>({});

const OuterElementType = React.forwardRef<HTMLDivElement>((props, ref) => {
  const { onScroll: onListboxScroll, ...outerProps } = React.useContext(OuterElementContext);
  const { onScroll: onVirtualizedScroll, ...virtualizedProps } = props;

  return (
    <div
      ref={ref}
      {...virtualizedProps}
      {...outerProps}
      onScroll={(event) => {
        onVirtualizedScroll?.(event);
        onListboxScroll?.(event);
      }}
    />
  );
});

function useResetCache(data: number) {
  const ref = React.useRef<VariableSizeList>(null);
  React.useEffect(() => {
    if (ref.current != null) {
      ref.current.resetAfterIndex(0, true);
    }
  }, [data]);
  return ref;
}

// Adapter for react-window
const ListboxComponent = React.forwardRef<HTMLDivElement, React.HTMLAttributes<HTMLElement>>(
  (props, ref) => {
    const { children, ...other } = props;
    const itemData: React.ReactChild[] = [];
    (children as React.ReactChild[]).forEach(
      (item: React.ReactChild & { children?: React.ReactChild[] }) => {
        itemData.push(item);
        itemData.push(...(item.children ?? []));
      }
    );

    const theme = useTheme();
    const smUp = useMediaQuery(theme.breakpoints.up('sm'), {
      noSsr: true
    });
    const itemCount = itemData.length;
    const itemSize = smUp ? 36 : 48;

    const getChildSize = (child: React.ReactChild) => {
      if (Object.hasOwn(child, 'group')) {
        return 48;
      }

      return itemSize;
    };

    const getHeight = () => {
      if (itemCount > 8) {
        return 8 * itemSize;
      }
      return itemData.map(getChildSize).reduce((a, b) => a + b, 0);
    };

    const gridRef = useResetCache(itemCount);

    return (
      <div ref={ref}>
        <OuterElementContext.Provider value={other}>
          <VariableSizeList
            ref={gridRef}
            height={getHeight() + 2 * 8}
            innerElementType="ul"
            itemCount={itemCount}
            itemData={itemData}
            itemSize={(index) => getChildSize(itemData[index])}
            outerElementType={OuterElementType}
            overscanCount={5}
            width="100%"
          >
            {RenderRow}
          </VariableSizeList>
        </OuterElementContext.Provider>
      </div>
    );
  }
);

const StyledPopper = styled(Popper)({
  [`& .${autocompleteClasses.listbox}`]: {
    boxSizing: 'border-box',
    '& ul': {
      padding: 0,
      margin: 0
    }
  }
});

const normalizeLegacyProps = (
  props: VirtualizeLegacyProps
): NormalizedVirtualizeProps<string, boolean> => {
  const {
    dataTestId = 'virtualize-demo',
    error,
    errorStyles,
    freeSolo,
    hasIdAndRef,
    label,
    loading,
    loadingText,
    passedStyles,
    passOptions,
    setValue,
    value = null
  } = props;

  return {
    dataTestId,
    disableClientFilter: false,
    disablePortal: true,
    error,
    errorStyles,
    forcePopupIcon: freeSolo,
    freeSolo: freeSolo ?? false,
    getOptionLabel: (option) => option,
    label,
    loading,
    loadingText,
    onChange: (event, newValue) => {
      const optionIndex =
        event.target instanceof HTMLElement ? event.target.dataset.optionIndex : undefined;
      const selectedValue = newValue ?? '';
      if (hasIdAndRef) {
        setValue(optionIndex ?? '', selectedValue);
        return;
      }
      setValue(selectedValue, selectedValue);
    },
    options: passOptions,
    required: false,
    styles: passedStyles,
    value
  };
};

const normalizeGenericProps = <T, FreeSolo extends boolean>(
  props: VirtualizeGenericProps<T, FreeSolo>
): NormalizedVirtualizeProps<T, FreeSolo> => {
  const {
    dataTestId = 'virtualize-demo',
    disableClientFilter = false,
    disablePortal = true,
    error,
    errorStyles,
    freeSolo,
    getOptionLabel,
    groupBy,
    innerRef,
    inputAutoComplete,
    inputRef,
    isOptionEqualToValue,
    label,
    loading,
    loadingText,
    noOptionsText,
    onChange,
    onInputChange,
    onListboxScroll,
    options,
    placeholder,
    required = false,
    styles,
    value
  } = props;

  return {
    dataTestId,
    disableClientFilter,
    disablePortal,
    error,
    errorStyles,
    forcePopupIcon: freeSolo,
    freeSolo,
    getOptionLabel,
    groupBy,
    innerRef,
    inputAutoComplete,
    inputRef,
    isOptionEqualToValue,
    label,
    loading,
    loadingText,
    noOptionsText,
    onChange: async (_event, newValue) => onChange(newValue),
    onInputChange: onInputChange
      ? (_event, inputValue, reason) => onInputChange(inputValue, reason)
      : undefined,
    onListboxScroll,
    options,
    placeholder,
    required,
    styles,
    value
  };
};

const VirtualizedAutocomplete = <T, FreeSolo extends boolean>(
  props: NormalizedVirtualizeProps<T, FreeSolo>
) => {
  const {
    dataTestId,
    disableClientFilter,
    disablePortal,
    error,
    errorStyles,
    forcePopupIcon,
    freeSolo,
    getOptionLabel,
    groupBy,
    innerRef,
    inputAutoComplete,
    inputRef,
    isOptionEqualToValue,
    label,
    loading,
    loadingText,
    noOptionsText,
    onChange,
    onInputChange,
    onListboxScroll,
    options,
    placeholder,
    required,
    styles,
    value
  } = props;

  return (
    <Box sx={{ position: 'relative' }}>
      <Autocomplete<T, false, false, FreeSolo>
        disableListWrap
        data-testid={dataTestId}
        disablePortal={disablePortal}
        filterOptions={disableClientFilter ? (availableOptions) => availableOptions : undefined}
        forcePopupIcon={forcePopupIcon}
        freeSolo={freeSolo}
        getOptionLabel={getOptionLabel}
        groupBy={groupBy}
        id={dataTestId}
        isOptionEqualToValue={isOptionEqualToValue}
        loading={loading}
        loadingText={loadingText}
        noOptionsText={noOptionsText}
        onChange={onChange}
        onInputChange={onInputChange}
        options={options}
        renderGroup={(params) => params as unknown as React.ReactElement}
        sx={styles}
        value={value}
        renderInput={(params) => (
          <TextField
            {...params}
            ref={innerRef}
            inputRef={inputRef}
            label={label}
            placeholder={placeholder}
            required={required}
            sx={sharedClasses.autoCompleteTextfield}
            slotProps={{
              ...params.slotProps,
              inputLabel: { shrink: true },

              htmlInput: {
                ...params.slotProps.htmlInput,
                ...(inputAutoComplete ? { autoComplete: inputAutoComplete } : {})
              },

              input: {
                ...params.slotProps.input,
                endAdornment: (
                  <>
                    {loading ? <CircularProgress color="inherit" size={20} /> : null}
                    {params.slotProps.input.endAdornment}
                  </>
                )
              }
            }}
          />
        )}
        renderOption={(optionProps, option) =>
          [optionProps, getOptionLabel(option)] as unknown as React.ReactElement
        }
        slotProps={{
          popper: { component: StyledPopper },
          listbox: {
            component: ListboxComponent,
            ...(onListboxScroll ? { onScroll: onListboxScroll } : {}),
            style: sharedClasses.autoCompleteListStyles
          }
        }}
      />
      {error ? (
        <Box sx={errorStyles}>{typeof error === 'string' ? error : 'This field is required'}</Box>
      ) : null}
    </Box>
  );
};

export const Virtualize = <T, FreeSolo extends boolean = false>(
  props: VirtualizeProps<T, FreeSolo>
) => {
  if (isLegacyProps(props)) {
    return <VirtualizedAutocomplete {...normalizeLegacyProps(props)} />;
  }
  return <VirtualizedAutocomplete {...normalizeGenericProps(props)} />;
};
